import { useState, useEffect } from "react";
import { getToken } from "../api";

const BASE = window.location.hostname === "localhost"
  ? "http://localhost:8001/api"
  : `${window.location.protocol}//${window.location.host}/api`;

const authFetch = (url, opts = {}) => {
  const token = getToken();
  return fetch(url, {
    ...opts,
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
  });
};

export default function Backup({ theme }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [toast, setToast] = useState("");

  const D = theme === "dark";
  const C = {
    bg:      D ? "#0D0B08" : "#F5F0E8",
    card:    D ? "#141008" : "#FFFFFF",
    border:  D ? "#2A2418" : "#DDD5C5",
    text:    D ? "#F2EFE9" : "#1A1612",
    mute:    D ? "#6A5A40" : "#9A8A70",
    gold:    "#C9A870",
  };

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const fetchBackups = async () => {
    try {
      const r = await authFetch(`${BASE}/backup/list`);
      const data = await r.json();
      setBackups(data.backups || []);
    } catch (e) { showToast("Error al cargar backups"); }
  };

  useEffect(() => { fetchBackups(); }, []);

  const handleExport = async () => {
    setLoading(true);
    try {
      const token = getToken();
      const r = await fetch(`${BASE}/backup/export`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error("Error al exportar");
      const blob = await r.blob();
      const disposition = r.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `clavex_backup_${Date.now()}.json.gz`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      showToast("✓ Backup exportado correctamente");
      fetchBackups();
    } catch (e) { showToast("Error al exportar"); }
    finally { setLoading(false); }
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`¿Eliminar backup ${filename}?`)) return;
    try {
      await authFetch(`${BASE}/backup/${filename}`, { method: "DELETE" });
      showToast("Backup eliminado");
      fetchBackups();
    } catch (e) { showToast("Error al eliminar"); }
  };

  const handleRestore = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!window.confirm("¿Restaurar este backup? Se sobreescribirán los datos actuales.")) return;
    setRestoring(true);
    try {
      const token = getToken();
      const formData = new FormData();
      formData.append("file", file);
      const r = await fetch(`${BASE}/backup/restore`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      if (!r.ok) throw new Error("Error al restaurar");
      const data = await r.json();
      showToast(`✓ Restaurado: ${Object.entries(data.restored).map(([k,v]) => `${k}: ${v}`).join(", ")}`);
    } catch (e) { showToast("Error al restaurar"); }
    finally { setRestoring(false); e.target.value = ""; }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24, padding: "0 4px" }}>
      {toast && (
        <div style={{ position: "fixed", bottom: 28, left: "50%", transform: "translateX(-50%)", background: C.card, border: `1px solid ${C.gold}`, color: C.gold, padding: "11px 22px", borderRadius: 6, fontSize: 12, fontFamily: "Plus Jakarta Sans, sans-serif", letterSpacing: 1.5, textTransform: "uppercase", zIndex: 200 }}>
          {toast}
        </div>
      )}

      <div>
        <div style={{ fontSize: 10, letterSpacing: "0.2em", color: C.gold, textTransform: "uppercase", marginBottom: 8, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Sistema</div>
        <h1 style={{ fontSize: 32, fontWeight: 700, color: C.text, margin: 0, fontFamily: "Plus Jakarta Sans, sans-serif" }}>Copia de Seguridad</h1>
      </div>

      {/* Acciones */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
        <button onClick={handleExport} disabled={loading}
          style={{ background: C.gold, border: "none", color: "#0D0B08", padding: "10px 20px", borderRadius: 6, fontSize: 12, fontFamily: "Plus Jakarta Sans, sans-serif", fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", opacity: loading ? 0.6 : 1 }}>
          {loading ? "Exportando..." : "⬇ Exportar backup"}
        </button>

        <label style={{ background: "none", border: `1px solid ${C.border}`, color: C.mute, padding: "10px 20px", borderRadius: 6, fontSize: 12, fontFamily: "Plus Jakarta Sans, sans-serif", letterSpacing: 1.5, textTransform: "uppercase", cursor: "pointer", opacity: restoring ? 0.6 : 1 }}>
          {restoring ? "Restaurando..." : "⬆ Restaurar backup"}
          <input type="file" accept=".json.gz" onChange={handleRestore} style={{ display: "none" }} disabled={restoring} />
        </label>
      </div>

      {/* Lista de backups */}
      <div style={{ background: C.card, border: `0.5px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: `0.5px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 12, fontFamily: "Plus Jakarta Sans, sans-serif", letterSpacing: 1.5, color: C.gold, textTransform: "uppercase" }}>Backups guardados</span>
          <span style={{ fontSize: 11, color: C.mute, fontFamily: "Plus Jakarta Sans, sans-serif" }}>{backups.length} archivos</span>
        </div>
        {backups.length === 0 ? (
          <div style={{ padding: "32px", textAlign: "center", color: C.mute, fontSize: 13, fontFamily: "Plus Jakarta Sans, sans-serif", letterSpacing: 1 }}>
            Sin backups disponibles
          </div>
        ) : backups.map(b => (
          <div key={b.filename} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: `0.5px solid ${C.border}` }}>
            <div>
              <div style={{ fontSize: 13, color: C.text, fontFamily: "Plus Jakarta Sans, sans-serif", marginBottom: 3 }}>{b.filename}</div>
              <div style={{ fontSize: 11, color: C.mute, fontFamily: "Plus Jakarta Sans, sans-serif" }}>
                {new Date(b.created_at).toLocaleString("es-ES")} · {b.size_kb} KB
              </div>
            </div>
            <button onClick={() => handleDelete(b.filename)}
              style={{ background: "none", border: `0.5px solid rgba(139,58,58,0.4)`, color: "#8B3A3A", padding: "5px 12px", borderRadius: 4, fontSize: 11, fontFamily: "Plus Jakarta Sans, sans-serif", cursor: "pointer", letterSpacing: 1 }}>
              Eliminar
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
